import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ColumnDef,
  createAngularTable,
  ExpandedState,
  FlexRenderDirective,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  GroupingState,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableOptions,
  Updater,
} from '@tanstack/angular-table';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'sz-table',
    imports: [CommonModule, RouterModule, FlexRenderDirective],
    templateUrl: './sz-table.component.html',
    styleUrl: './sz-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
// skipcq: JS-0323
export class SzTableComponent<T = any> implements OnChanges {
  // --- Inputs ---
  @Input() data: T[] = [];
  @Input() columns: ColumnDef<T, any>[] = []; // skipcq: JS-0323
  @Input() enableSelection = false;
  @Input() selectionMode: 'single' | 'multiple' = 'multiple';
  @Input() enableSorting = true;
  @Input() enableVirtual = false;
  @Input() virtualRowHeight = 36;
  @Input() virtualHeight = '500px';
  @Input() groupBy: string | string[] = '';
  @Input() enablePaging = false;
  @Input() pageSize = 20;
  @Input() emptyMessage = 'No data available';
  @Input() emptyActionLabel?: string;
  @Input() emptyActionRoute?: string;
  @Input() rowClassFn?: (row: T) => string;
  @Input() cellClassFn?: (row: T, columnId: string) => string;
  @Input() enableFooter = false;
  @Input() getRowId?: (row: T) => string;
  @Input() maxHeight = '';
  @Input() enableGroupExpand = true;
  @Input() showSelectAll = true;
  // skipcq: JS-0323
  @Input() cellTemplates: { [columnId: string]: TemplateRef<any> | null } = {};
  // skipcq: JS-0323
  @Input() groupLabelFn?: (value: any) => string | null;
  @Input() headerClassFn?: (headerId: string) => string;
  @Input() enableFiltering = false;
  @Input() enableStriping = false;

  // --- Outputs ---
  @Output() selectionChange = new EventEmitter<T[]>();
  @Output() sortingChange = new EventEmitter<SortingState>();

  // --- Internal state signals ---
  readonly dataSignal = signal<T[]>([]);
  readonly sorting = signal<SortingState>([]);
  readonly rowSelection = signal<RowSelectionState>({});
  readonly grouping = signal<GroupingState>([]);
  readonly expanded = signal<ExpandedState>(true);
  readonly pagination = signal<PaginationState>({ pageIndex: 0, pageSize: 20 });
  readonly globalFilter = signal<string>('');

  // --- Virtual scroll ref ---
  readonly scrollElement = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  // --- Table instance ---
  table = createAngularTable(() => {
    const tableOpts: TableOptions<T> = {
      data: this.dataSignal(),
      columns: this.columns,
      state: {
        sorting: this.sorting(),
        rowSelection: this.rowSelection(),
        grouping: this.grouping(),
        expanded: this.expanded(),
        ...(this.enablePaging ? { pagination: this.pagination() } : {}),
        ...(this.enableFiltering ? { globalFilter: this.globalFilter() } : {}),
      },
      enableRowSelection: this.enableSelection,
      enableMultiRowSelection: this.enableSelection && this.selectionMode === 'multiple',
      enableSorting: this.enableSorting,
      enableGrouping: !!this.groupBy,
      onSortingChange: (updater: Updater<SortingState>) => {
        const next = typeof updater === 'function' ? updater(this.sorting()) : updater;
        this.sorting.set(next);
        this.sortingChange.emit(next);
      },
      onRowSelectionChange: (updater: Updater<RowSelectionState>) => {
        const next = typeof updater === 'function' ? updater(this.rowSelection()) : updater;
        this.rowSelection.set(next);
        const rows = this.table.getSelectedRowModel().rows;
        this.selectionChange.emit(rows.map(r => r.original));
      },
      onGroupingChange: (updater: Updater<GroupingState>) => {
        const next = typeof updater === 'function' ? updater(this.grouping()) : updater;
        this.grouping.set(next);
      },
      onExpandedChange: (updater: Updater<ExpandedState>) => {
        const next = typeof updater === 'function' ? updater(this.expanded()) : updater;
        this.expanded.set(next);
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      ...(this.enableFiltering ? { getFilteredRowModel: getFilteredRowModel() } : {}),
    };

    if (this.getRowId) {
      tableOpts.getRowId = (row: T) => this.getRowId!(row);
    }

    if (this.groupBy) {
      tableOpts.getGroupedRowModel = getGroupedRowModel();
      tableOpts.getExpandedRowModel = getExpandedRowModel();
    }

    if (this.enablePaging) {
      tableOpts.getPaginationRowModel = getPaginationRowModel();
      tableOpts.onPaginationChange = (updater: Updater<PaginationState>) => {
        const next = typeof updater === 'function' ? updater(this.pagination()) : updater;
        this.pagination.set(next);
      };
    }

    return tableOpts;
  });

  // --- Virtual rows ---
  virtualizer = injectVirtualizer(() => ({
    scrollElement: this.scrollElement()?.nativeElement ?? undefined,
    count: this.enableVirtual ? this.table.getRowModel().rows.length : 0,
    estimateSize: () => this.virtualRowHeight,
    overscan: 20,
  }));

  // --- Visible rows (virtual or all) ---
  // Explicitly tracks dataSignal so this computed re-evaluates after ngOnChanges
  // sets new data — this forces createAngularTable's lazy computed chain to flush.
  readonly visibleRows = computed(() => {
    this.dataSignal(); // track data changes
    return this.table.getRowModel().rows;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.dataSignal.set(this.data ?? []);
    }
    if (changes['groupBy']) {
      const g = this.groupBy;
      this.grouping.set(g ? (Array.isArray(g) ? g : [g]) : []);
    }
    if (changes['pageSize']) {
      this.pagination.update(p => ({ ...p, pageSize: this.pageSize }));
    }
  }

  clearSelection(): void {
    this.rowSelection.set({});
  }

  getRowClasses(row: Row<T>): string {
    if (!this.rowClassFn || row.getIsGrouped()) return '';
    return this.rowClassFn(row.original);
  }

  getCellClasses(row: Row<T>, columnId: string): string {
    if (!this.cellClassFn || row.getIsGrouped()) return '';
    return this.cellClassFn(row.original, columnId);
  }

  getRowAtIndex(index: number): Row<T> | undefined {
    return this.visibleRows()[index];
  }

  get pageCount(): number {
    return this.table.getPageCount();
  }

  get currentPage(): number {
    return this.pagination().pageIndex;
  }

  goToPage(index: number): void {
    this.table.setPageIndex(index);
  }

  nextPage(): void {
    this.table.nextPage();
  }

  previousPage(): void {
    this.table.previousPage();
  }

  onFilterChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.globalFilter.set(value);
  }
}
